// Web MIDI: any class-compliant keyboard connects automatically, including
// ones plugged in after the page loads. Not available in Safari.

export interface MidiHandlers {
  noteOn(note: number, velocity: number): void;
  noteOff(note: number): void;
  sustain(on: boolean): void;
  /** -1..1 */
  bend(amount: number): void;
  /** 0..1 */
  mod(amount: number): void;
  allOff(): void;
  status(s: MidiStatus): void;
}

export type MidiStatus =
  | { kind: 'unsupported' }
  | { kind: 'denied' }
  | { kind: 'waiting' }
  | { kind: 'connected'; names: string[] };

export function parseMidi(data: Uint8Array, h: MidiHandlers): void {
  const [status, d1 = 0, d2 = 0] = data;
  const type = status & 0xf0;
  if (type === 0x90 && d2 > 0) h.noteOn(d1, d2);
  else if (type === 0x80 || (type === 0x90 && d2 === 0)) h.noteOff(d1);
  else if (type === 0xe0) h.bend((((d2 << 7) | d1) - 8192) / 8192);
  else if (type === 0xb0) {
    if (d1 === 64) h.sustain(d2 >= 64);
    else if (d1 === 1) h.mod(d2 / 127);
    else if (d1 === 120 || d1 === 123) h.allOff();
  }
}

export async function attachMidi(h: MidiHandlers): Promise<void> {
  if (!('requestMIDIAccess' in navigator)) {
    h.status({ kind: 'unsupported' });
    return;
  }
  let access: MIDIAccess;
  try {
    access = await navigator.requestMIDIAccess({ sysex: false });
  } catch {
    h.status({ kind: 'denied' });
    return;
  }
  const bind = () => {
    const names: string[] = [];
    access.inputs.forEach((input) => {
      if (input.state !== 'connected') return;
      names.push(input.name ?? 'MIDI keyboard');
      input.onmidimessage = (e) => e.data && parseMidi(e.data, h);
    });
    h.status(names.length ? { kind: 'connected', names } : { kind: 'waiting' });
  };
  access.onstatechange = (e) => {
    // Unplugging mid-note would otherwise leave it ringing forever.
    const port = (e as MIDIConnectionEvent).port;
    if (port?.type === 'input' && port.state === 'disconnected') h.allOff();
    bind();
  };
  bind();
}
